import React, { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useFramework, useScale } from '@/framework';
import { base64Kit } from '@/utils/base64-kit';
import type { IBackgroundLayerProps, ILayerProps } from '@/version-types';
import bgImage from '../assets/bg.png';
import './crop-background-operate.less';
import { type CropBackground } from '../crop-background';
import { CROP_BACKGROUND_EVENTS } from '../event-name';

const CropBackgroundOperate: React.FC = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const scale = useScale();
    const framework = useFramework();
    const cropBackground = framework.service.registration.getInstance<CropBackground>('CropBackground');
    const [activeLayer, setActiveLayer] = useState<ILayerProps>(cropBackground.getLatestLayer()!);
    const { width: stageWidth, height: stageHeight } = activeLayer;
    const divRef = useRef<HTMLDivElement>(null);
    const {
        url,
        imageTransform: { scale: imgScale, flipV, flipH, widthRatio, heightRatio, offsetX, offsetY, rotate: imgRotate },
    } = activeLayer.data as IBackgroundLayerProps;

    // 拖动相关属性
    const dragBgRef = useRef({ x: 0, y: 0 });
    const drag = useRef({
        imgX: 0,
        imgY: 0,
        isMove: false,
        startPos: { x: 0, y: 0 },
        movePos: { x: 0, y: 0 },
        layer: {} as ILayerProps,
        imgMaxX: 0,
        imgMaxY: 0,
        imgScale: 0,
        scale: 0,
    });

    useEffect(() => {
        const drawImage = () => {
            const { width: stageWidth, height: stageHeight } = drag.current.layer;
            const data = drag.current.layer.data as IBackgroundLayerProps;
            const {
                imageTransform: { widthRatio, heightRatio },
            } = data;
            let { imgMaxX, imgMaxY } = drag.current;
            const { imgX, imgY, imgScale, scale } = drag.current;
            imgMaxX *= scale;
            imgMaxY *= scale;
            const x = Math.abs(imgX) > imgMaxX ? imgMaxX * (imgX < 0 ? -1 : 1) : imgX;
            const y = Math.abs(imgY) > imgMaxY ? imgMaxY * (imgY < 0 ? -1 : 1) : imgY;
            data.imageTransform.offsetX = x / stageWidth / widthRatio / scale / imgScale;
            data.imageTransform.offsetY = y / stageHeight / heightRatio / scale / imgScale;

            cropBackground.setLatestLayer({ ...drag.current.layer });
        };

        const startMouse = (e: MouseEvent) => {
            drag.current.isMove = true;
            drag.current.startPos = { x: e.pageX, y: e.pageY };
            if (containerRef.current) containerRef.current.style.cursor = 'move';
        };

        const moveMouse = (e: MouseEvent) => {
            if (!drag.current.isMove) return;
            const {
                imageTransform: { flipV, flipH },
            } = drag.current.layer.data as IBackgroundLayerProps;
            drag.current.movePos = { x: e.pageX, y: e.pageY };
            const { movePos, startPos } = drag.current;
            const directionX = flipH ? -1 : 1;
            const directionY = flipV ? -1 : 1;
            drag.current.imgX += (movePos.x - startPos.x) * directionX;
            drag.current.imgY += (movePos.y - startPos.y) * directionY;
            dragBgRef.current.x = drag.current.imgX;
            dragBgRef.current.y = drag.current.imgY;
            drag.current.startPos = { ...movePos }; // 更新最新位置
            drawImage();
        };

        const endMouse = () => {
            drag.current.isMove = false;
            if (containerRef.current) containerRef.current.style.cursor = '';
            drawImage();
        };

        const div = divRef.current;
        div?.addEventListener('mousedown', startMouse, true);
        document?.addEventListener('mousemove', moveMouse, true);
        document?.addEventListener('mouseup', endMouse, true);

        return () => {
            div?.removeEventListener('mousedown', startMouse, true);
            document?.removeEventListener('mousemove', moveMouse, true);
            document?.removeEventListener('mouseup', endMouse, true);
        };
    }, []);

    useEffect(() => {
        const handler = framework.service.events.on(CROP_BACKGROUND_EVENTS.SET_LATEST_LAYER, (layer: ILayerProps) => {
            setActiveLayer(layer);
        });

        return () => {
            framework.service.events.off(handler);
        };
    }, []);

    const imageSrc = base64Kit.getLocalUrl(url);
    drag.current.imgX = offsetX * imgScale * stageWidth * widthRatio * scale;
    drag.current.imgY = offsetY * imgScale * stageHeight * heightRatio * scale;
    drag.current.imgMaxX = (stageWidth * widthRatio * imgScale - stageWidth) / 2;
    drag.current.imgMaxY = (stageHeight * heightRatio * imgScale - stageHeight) / 2;
    drag.current.imgScale = imgScale;
    drag.current.layer = activeLayer;
    drag.current.scale = scale;
    dragBgRef.current.x = drag.current.imgX;
    dragBgRef.current.y = drag.current.imgY;

    const rotateX = flipV ? 180 : 0;
    const rotateY = flipH ? 180 : 0;
    const rotateZ = 0;

    if (!imageSrc) return null;

    const imageCss: CSSProperties = {
        width: widthRatio * stageWidth * scale + 'px',
        height: heightRatio * stageHeight * scale + 'px',
        transform: `translateX(${dragBgRef.current.x}px) translateY(${dragBgRef.current.y}px) rotateZ(${rotateZ}deg) scale(${imgScale}) rotate(${imgRotate}deg)`,
        pointerEvents: 'none',
        flexShrink: 0,
    };

    const containerCss: CSSProperties = {
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: `translate(-50%, -50%) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`,
        width: `${stageWidth * scale}px`,
        height: `${stageHeight * scale}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    };

    return (
        <div ref={containerRef} className="l-crop-operate-background">
            <div
                ref={divRef}
                style={{
                    ...containerCss,
                    overflow: 'hidden',
                    zIndex: 1,
                    backgroundImage: `url(${bgImage})`,
                    backgroundRepeat: 'repeat',
                    cursor: 'move',
                }}
            >
                <img src={imageSrc} alt="" style={imageCss} />
            </div>
            {/* 蒙层 */}
            <div style={{ ...containerCss, zIndex: -1 }}>
                <img src={imageSrc} alt="" style={{ ...imageCss, opacity: 0.3 }} />
            </div>
        </div>
    );
};

export default CropBackgroundOperate;
